import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../styles/gridFontSettings.module.scss';
import { GridFontSettings, GridFontWeight } from '../../../../libs/ui-settings-manager/interfaces';
import { DEFAULT_GRID_FONT_SETTINGS, UISettingsManager } from '../../../../libs/ui-settings-manager/UISettingsManager';
import { BaseModalWindow } from '../../../../components/modalWindows/BaseModalWindow';
import GridWrapper from '../../../../components/controls/GridWrapper';
import { Button } from '../../../../components/buttons/button';

interface IGridFontSettingsModalProps {
    onClose: () => void;
}

const WEIGHT_OPTIONS: { value: GridFontWeight; labelKey: string }[] = [
    { value: 400, labelKey: 'modals.gridFontSettings.weight.regular' },
    { value: 700, labelKey: 'modals.gridFontSettings.weight.bold' },
];

const SIZE_PRESETS = [10, 12, 14, 16, 18, 20];

const COLOR_PRESETS = ['#000000', '#000000d9', '#333333', '#666666'];

const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 20;

export const GridFontSettingsModal = ({ onClose }: IGridFontSettingsModalProps) => {
    const { t } = useTranslation();

    const [font, setFont] = useState<GridFontSettings>(
        () => UISettingsManager.getSettings().grids.font
    );

    const [draftSize, setDraftSize] = useState<string>(String(font.fontSize));

    useEffect(() => {
        setDraftSize(String(font.fontSize));
    }, [font.fontSize]);

    const commitSize = () => {
        const parsed = Number(draftSize);
        const clamped =
            Number.isNaN(parsed) || draftSize === ''
                ? MIN_FONT_SIZE
                : Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, Math.round(parsed)));

        if (clamped !== font.fontSize) {
            setFont(prev => ({ ...prev, fontSize: clamped }));
        }
        setDraftSize(String(clamped));
    };

    const handleReset = () => {
        const defaults = DEFAULT_GRID_FONT_SETTINGS
        setFont(defaults);
    };

    const handleSave = () => {
        UISettingsManager.updateSection('grids', { font });
        onClose();
    };

    return (
        <BaseModalWindow
            header={t('modals.gridFontSettings.header')}
            ok={{ onClick: handleSave, title: t('general.save') }}
            cancel={{ onClick: onClose, title: t('general.cancel') }}
            className="documentTabSettings"
        >
            <div className={styles.container}>
                <GridWrapper cols={1} className={styles.controls}>
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="gridFontSize">
                            {t('modals.gridFontSettings.fontSize')}
                        </label>
                        <div className={styles.sizeRow}>
                            <input
                                id="gridFontSize"
                                type="number"
                                min={MIN_FONT_SIZE}
                                max={MAX_FONT_SIZE}
                                step={1}
                                value={draftSize}
                                onChange={(e) => setDraftSize(e.target.value)}
                                onBlur={commitSize}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        (e.target as HTMLInputElement).blur();
                                    }
                                }}
                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                className={styles.numberInput}
                            />
                            <div className={styles.presets}>
                                {SIZE_PRESETS.map((size) => (
                                    <button
                                        key={size}
                                        type="button"
                                        className={`${styles.presetButton} ${font.fontSize === size ? styles.presetActive : ''
                                            }`}
                                        onClick={() => setFont({ ...font, fontSize: size })}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="gridFontWeight">
                            {t('modals.gridFontSettings.fontWeight')}
                        </label>
                        <select
                            id="gridFontWeight"
                            value={font.fontWeight}
                            onChange={(e) =>
                                setFont({
                                    ...font,
                                    fontWeight: Number(e.target.value) as GridFontWeight,
                                })
                            }
                            className={styles.select}
                        >
                            {WEIGHT_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {t(o.labelKey)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="gridFontColor">
                            {t('modals.gridFontSettings.fontColor')}
                        </label>
                        <div className={styles.colorRow}>
                            <input
                                id="gridFontColor"
                                type="color"
                                value={font.color}
                                onChange={(e) =>
                                    setFont({ ...font, color: e.target.value })
                                }
                                className={styles.colorInput}
                            />
                            <input
                                type="text"
                                value={font.color}
                                onChange={(e) =>
                                    setFont({ ...font, color: e.target.value })
                                }
                                className={styles.colorText}
                                aria-label={t('modals.gridFontSettings.fontColor')}
                            />
                            <div className={styles.colorPresets}>
                                {COLOR_PRESETS.map((color) => (
                                    <button
                                        key={color}
                                        type="button"
                                        className={`${styles.colorSwatch} ${
                                            font.color.toLowerCase() === color.toLowerCase()
                                                ? styles.colorSwatchActive
                                                : ''
                                        }`}
                                        style={{ backgroundColor: color }}
                                        onClick={() => setFont({ ...font, color })}
                                        aria-label={color}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </GridWrapper>
                <div className={styles.previewSection}>
                    <div className={styles.previewTitleContainer}>
                        <p className={styles.previewTitle}>
                            {t('modals.gridFontSettings.preview')}
                        </p>
                        <p className={styles.previewTitle}>
                            {t('modals.gridFontSettings.rowHeight')}
                        </p>
                    </div>
                    <div
                        className={styles.previewTable}
                        style={{
                            fontSize: font.fontSize,
                            fontWeight: font.fontWeight,
                            color: font.color,
                        }}
                    >
                        <div className={`${styles.previewRow} ${styles.previewRow_30}`}>
                            <span>Будь здоров! Валериана экстра драже 130мг N50</span>
                            <span className={styles.previewHint}>Сжатый</span>
                        </div>
                        <div className={`${styles.previewRow} ${styles.previewRow_36}`}>
                            <span>Будь здоров! Валериана экстра драже 130мг N50</span>
                            <span className={styles.previewHint}>Обычный</span>
                        </div>
                        <div className={`${styles.previewRow} ${styles.previewRow_44}`}>
                            <span>Будь здоров! Валериана экстра драже 130мг N50</span>
                            <span className={styles.previewHint}>Разряженный</span>
                        </div>
                    </div>
                </div>
                <Button
                    onClick={handleReset}
                    variant="outprimary"
                >
                    {t('modals.gridFontSettings.reset')}
                </Button>
            </div>
        </BaseModalWindow>
    );
};